import { WorktypeAnalysisRating } from '@/features/types';
import type { WorkTypeAnalysis } from '@/generated/types/navigator/types.gen';
import {
  metersToFeet,
  squareMetersToSquareFeet,
} from '@/helpers/distanceMetric';

export type WorkTypeView = 'area' | 'linear';
export type WorkTypeUnit = 'sqft' | 'sqm' | 'ft' | 'm';

export interface WorkTypeRow {
  rating: WorktypeAnalysisRating;
  amount: number;
  unit: WorkTypeUnit;
  /** Unrounded percent of the span buffer (area) or of the span line (linear). */
  percent: number;
}

const ROW_ORDER = [
  WorktypeAnalysisRating.Cut_Zone,
  WorktypeAnalysisRating.Mow_Zone,
  WorktypeAnalysisRating.Herbicide_Zone,
  WorktypeAnalysisRating.Side_Wall,
  WorktypeAnalysisRating.Not_Vegetation,
];

/**
 * One row per work type, in display order, from the API's metres. Linear percentages are each
 * work type's own share of the line, so together they can pass 100% where work types sit side
 * by side. "No work type" is the buffer left by the classes, or the line no class shadows.
 */
export const formatWorkTypeRows = (
  analysis: WorkTypeAnalysis,
  view: WorkTypeView,
  metric: 'ft' | 'm'
): WorkTypeRow[] => {
  const imperial = metric === 'ft';
  const toClientUnit = (m: number) =>
    !imperial
      ? m
      : view === 'area'
        ? squareMetersToSquareFeet(m)
        : metersToFeet(m);
  const unit: WorkTypeUnit =
    view === 'area' ? (imperial ? 'sqft' : 'sqm') : imperial ? 'ft' : 'm';
  const total =
    view === 'area' ? analysis.buffer_area_m2 : analysis.line_length_m;

  const byRating = new Map(
    analysis.classes.map((c) => [c.worktype_class as WorktypeAnalysisRating, c])
  );
  const classArea = analysis.classes.reduce((sum, c) => sum + c.area_m2, 0);
  const metres = (rating: WorktypeAnalysisRating): number => {
    if (rating === WorktypeAnalysisRating.Not_Vegetation) {
      return view === 'area'
        ? Math.max(analysis.buffer_area_m2 - classArea, 0)
        : analysis.uncovered_linear_m;
    }
    const row = byRating.get(rating);
    return view === 'area' ? (row?.area_m2 ?? 0) : (row?.linear_m ?? 0);
  };

  return ROW_ORDER.map((rating) => ({
    rating,
    amount: Math.round(toClientUnit(metres(rating))),
    unit,
    percent: total > 0 ? (metres(rating) / total) * 100 : 0,
  }));
};
